import { defineStore } from "pinia";
import { ref, watch } from "vue";
import { jwtDecode } from "jwt-decode";
import { auth } from "@/auth";
import { useAuthSessionStore } from "@/stores/authSession";
import { backoffDelayMs } from "@common-ui/utilities/retry";

/**
 * LSP-18855: a rejected SSE handshake used to be reported as a bare
 * `SSE connection failed: 401` and nothing else, which is not diagnosable. In production this hit
 * 376 distinct sessions over 29 days and the reconstruction needed to separate the three candidate
 * causes — audience/issuer mismatch against scribe, expiry at the boundary, or org/claim scoping —
 * could not be done from the recorded data at all.
 *
 * These constants and helpers exist purely to make the NEXT occurrence self-explanatory, per
 * CLAUDE.md's "Diagnostic logging when fixing silent failures". They never change control flow.
 */
const AUTH_DIAGNOSTIC_BODY_TIMEOUT_MS = 2_000;

/**
 * The response body, bounded.
 *
 * A resolved `fetch` only guarantees the HEADERS arrived — the body read is a second, independently
 * stallable await. A diagnostic must never be able to delay a reconnect, so the read races a timer
 * and the reconnect proceeds either way.
 */
async function readBoundedBody(response: Response, abortStalledRead: () => void): Promise<string> {
	// Read the body DIRECTLY rather than via response.clone(). Cloning tees the stream, and a tee whose
	// second branch is never consumed keeps the connection pinned open — which on a persistently
	// rejected handshake means one pinned socket per reconnect attempt, forever. Nothing else reads
	// this body: the caller throws immediately afterwards.
	//
	// The rejection handler is attached HERE, not on the race, because aborting below makes this
	// promise reject *after* the race has already settled. Without it that late rejection would be
	// unattended, and a diagnostic must never produce an unhandled promise rejection.
	const read = response.text().then(
		(text) => text.slice(0, 500),
		() => "(body unavailable)"
	);

	let timer: ReturnType<typeof setTimeout> | undefined;
	try {
		return await Promise.race([
			read,
			new Promise<string>((resolve) => {
				timer = setTimeout(() => {
					// `response.text()` holds an EXCLUSIVE lock on the body, so no external caller can
					// cancel a stalled read — `response.body.cancel()` throws while it is locked. Aborting
					// the fetch is the only way to release the socket. Safe here: the caller is about to
					// throw and reconnect, and the reconnect mints a fresh AbortController.
					abortStalledRead();
					resolve("(body read timed out)");
				}, AUTH_DIAGNOSTIC_BODY_TIMEOUT_MS);
			})
		]);
	} finally {
		// Always cleared — when the read wins, an uncleared timer would keep its closure (and this
		// response) reachable for the full timeout on every rejected attempt.
		clearTimeout(timer);
	}
}

/**
 * The token's audience, issuer and remaining lifetime — the three facts that separate the candidate
 * causes of a scribe 401.
 *
 * Deliberately returns claim IDENTIFIERS only and never the token, any part of it, or any
 * user-identifying claim: this string is logged and forwarded to Datadog.
 */
function describeTokenForDiagnostics(token: string): Record<string, unknown> {
	try {
		const claims = jwtDecode<{ aud?: unknown; iss?: unknown; exp?: unknown }>(token);
		const audience = Array.isArray(claims.aud)
			? claims.aud.filter((entry): entry is string => typeof entry === "string").join(",")
			: typeof claims.aud === "string"
				? claims.aud
				: "(none)";
		return {
			tokenAudience: audience,
			tokenIssuer: typeof claims.iss === "string" ? claims.iss : "(none)",
			// Negative means the token was already expired when we sent it — which would make expiry,
			// not configuration, the cause.
			tokenExpiresInSeconds: typeof claims.exp === "number" ? Math.round(claims.exp - Date.now() / 1000) : null
		};
	} catch {
		return { tokenAudience: "(undecodable)", tokenIssuer: "(undecodable)", tokenExpiresInSeconds: null };
	}
}

export type ScribeEventType =
	| "analysis:created"
	| "analysis:updated"
	| "analysis:deleted"
	| "assistant:progress"
	| "assistant:complete"
	| "assistant:error"
	| "assistant:token"
	| "connected"
	| "heartbeat"
	| "token:refreshed"
	| "token:reauth-required"
	| "analysis:pdf-ready"
	| "analysis:pdf-failed";

export type ScribeEvent = {
	type: ScribeEventType;
	analysisId?: string;
	status?: string;
	connectionId?: string;
	accessToken?: string;
	[key: string]: unknown;
};

export interface ScribeEventHandlers {
	onAnalysisCreated?: (event: ScribeEvent) => void;
	onAnalysisUpdated?: (event: ScribeEvent) => void;
	onAnalysisDeleted?: (event: ScribeEvent) => void;
	onConnected?: (event: ScribeEvent) => void;
	onAnalysisPdfReady?: (event: ScribeEvent) => void;
	onAnalysisPdfFailed?: (event: ScribeEvent) => void;
}

type HandlerKey = keyof ScribeEventHandlers;

export const useEventsStore = defineStore("events", () => {
	const isConnected = ref(false);
	const isConnecting = ref(false);
	const error = ref<Error | null>(null);
	const reconnectAttempts = ref(0);
	const connectionId = ref<string | null>(null);

	const listenerSets = new Set<ScribeEventHandlers>();

	let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
	let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
	let abortController: AbortController | null = null;
	let heartbeatTimeout: ReturnType<typeof setTimeout> | null = null;
	let connectionAgeTimeout: ReturnType<typeof setTimeout> | null = null;
	let isIntentionalTeardown = false;
	let connectionEpoch = 0;
	// LSP-16971: set when we proactively halt because the session is terminally lost, so the
	// sessionLost watcher below auto-reconnects on IN-PLACE recovery (a background/cross-tab token
	// refresh that clears sessionLost without a page reload) instead of staying dark (Codex).
	let haltedForSessionLost = false;

	const lastActivityTime = ref(0);

	const HEARTBEAT_TIMEOUT_MS = 45_000; // Expect heartbeat every ~30s; stale after 45s
	const MAX_CONNECTION_AGE_MS = 30 * 60_000; // Force reconnect after 30 minutes

	/** Returns true if the error is an expected disconnect (network TypeError or AbortError from intentional teardown). */
	const isExpectedDisconnect = (err: unknown): boolean =>
		err instanceof TypeError || (err instanceof DOMException && err.name === "AbortError");

	const addHandlers = (next: ScribeEventHandlers): (() => void) => {
		listenerSets.add(next);
		return () => {
			listenerSets.delete(next);
		};
	};

	const emit = (key: HandlerKey, event: ScribeEvent) => {
		for (const handlers of listenerSets) {
			handlers[key]?.(event);
		}
	};

	const attemptReconnect = () => {
		// Clear any already-scheduled reconnect FIRST — so halting on a terminal session below
		// also cancels a reconnect armed while auth was still transient, leaving no live timer
		// after the halt (Bugbot).
		if (reconnectTimeout) {
			clearTimeout(reconnectTimeout);
			reconnectTimeout = null;
		}

		// LSP-16971: never spin reconnecting once the session is terminally lost — the
		// session-lost banner owns recovery (re-login re-arms auth and a fresh connect).
		if (useAuthSessionStore().sessionLost) {
			console.warn("[Events SSE] Session lost — halting reconnect until re-auth");
			haltedForSessionLost = true;
			return;
		}

		reconnectAttempts.value++;
		// LSP-17090: jittered (50-100%) exponential backoff — an unjittered delay reconnects the
		// whole tenant in lockstep against scribe/SSE after any server blip (LSP-17085 herd).
		const delay = backoffDelayMs(reconnectAttempts.value - 1, 1000, 30000);
		console.log(`[Events SSE] Reconnecting in ${delay}ms (attempt ${reconnectAttempts.value})`);

		reconnectTimeout = setTimeout(() => {
			void connect();
		}, delay);
	};

	/** Starts (or restarts) the heartbeat monitor that detects stale connections. */
	const startHeartbeatMonitor = () => {
		stopHeartbeatMonitor();
		lastActivityTime.value = Date.now();

		heartbeatTimeout = setTimeout(() => {
			const elapsed = Date.now() - lastActivityTime.value;
			if (elapsed >= HEARTBEAT_TIMEOUT_MS) {
				console.warn("[Events SSE] No heartbeat received — connection stale, reconnecting");
				forceReconnect();
			}
		}, HEARTBEAT_TIMEOUT_MS);
	};

	const stopHeartbeatMonitor = () => {
		if (heartbeatTimeout) {
			clearTimeout(heartbeatTimeout);
			heartbeatTimeout = null;
		}
	};

	/** Starts the max-age timer that forces a reconnect after MAX_CONNECTION_AGE_MS. */
	const startConnectionAgeTimer = () => {
		stopConnectionAgeTimer();
		connectionAgeTimeout = setTimeout(() => {
			console.log("[Events SSE] Max connection age reached, recycling connection");
			forceReconnect();
		}, MAX_CONNECTION_AGE_MS);
	};

	const stopConnectionAgeTimer = () => {
		if (connectionAgeTimeout) {
			clearTimeout(connectionAgeTimeout);
			connectionAgeTimeout = null;
		}
	};

	/** Gracefully tears down the current connection and immediately reconnects (no backoff). */
	const forceReconnect = () => {
		connectionEpoch++;
		isIntentionalTeardown = true;

		// The current id dies with this teardown — clear it so consumers that
		// wait for a connection id never grab one bound to a torn-down stream.
		connectionId.value = null;

		// Abort the in-flight fetch / stream
		if (abortController) {
			abortController.abort();
			abortController = null;
		}
		if (reader) {
			reader.cancel().catch(() => {});
			reader = null;
		}

		stopHeartbeatMonitor();
		stopConnectionAgeTimer();

		if (reconnectTimeout) {
			clearTimeout(reconnectTimeout);
			reconnectTimeout = null;
		}

		isConnected.value = false;
		isConnecting.value = false;
		reconnectAttempts.value = 0; // Reset backoff — this is a proactive reconnect

		attemptReconnect();
	};

	const handleEvent = (data: ScribeEvent) => {
		// Update activity tracking on every received event (AC-1)
		lastActivityTime.value = Date.now();
		startHeartbeatMonitor();

		switch (data.type) {
			case "analysis:created":
				emit("onAnalysisCreated", data);
				break;
			case "analysis:updated":
				emit("onAnalysisUpdated", data);
				break;
			case "analysis:deleted":
				emit("onAnalysisDeleted", data);
				break;
			case "analysis:pdf-ready":
				emit("onAnalysisPdfReady", data);
				break;
			case "analysis:pdf-failed":
				emit("onAnalysisPdfFailed", data);
				break;
			case "connected":
				console.log("[Events SSE] Connection ID received:", data.connectionId);
				connectionId.value = data.connectionId || null;
				emit("onConnected", data);
				break;
			case "assistant:progress":
			case "assistant:complete":
			case "assistant:error":
			case "assistant:token":
				window.dispatchEvent(new CustomEvent(data.type, { detail: data }));
				break;
			case "heartbeat":
				break;
			default:
				break;
		}
	};

	const connect = async () => {
		if (isConnecting.value || isConnected.value) return;
		const currentEpoch = ++connectionEpoch;

		if (!window.env.VITE_SCRIBE_DEV_TOKEN) {
			const isAuthenticated = await auth.isAuthenticated();

			if (!isAuthenticated) {
				console.log("[Events SSE] Not authenticated, skipping connection");
				return;
			}
		}

		// Clear intentional teardown only once this connect attempt actually proceeds.
		// This avoids stale abort handlers from a previous disconnect seeing `false`.
		isIntentionalTeardown = false;

		isConnecting.value = true;
		error.value = null;

		try {
			const devToken = window.env.VITE_SCRIBE_DEV_TOKEN;
			let token: string | null;

			if (devToken) {
				token = devToken;
			} else {
				if (auth.runningCallback) {
					await auth.waitForCallbackToFinish();
				}
				// LSP-16971: never let a background SSE reconnect trigger a full-page login
				// redirect — suppress it and recover via the session-lost banner instead.
				token = await auth.getAccessToken({ redirectOnAuthFailure: false });
			}

			if (!token) {
				// Terminal (banner is up) → stop; transient → throw so the catch backs off.
				if (useAuthSessionStore().sessionLost) {
					console.warn("[Events SSE] No token and session is lost — not connecting until re-auth");
					haltedForSessionLost = true;
					isConnecting.value = false;
					return;
				}
				console.warn("[Events SSE] No token available but session is not (yet) terminal — backing off and retrying");
				throw new Error("Authentication token not available");
			}

			const url = window.env.VITE_SCRIBE_URL + "/api/events";
			console.log("[Events SSE] Connecting to:", url);

			// AC-3: AbortController to proactively close stuck connections
			abortController = new AbortController();

			const response = await fetch(url, {
				method: "GET",
				headers: {
					Authorization: `Bearer ${token}`,
					Accept: "text/event-stream",
					"Cache-Control": "no-cache"
				},
				signal: abortController.signal
			});

			if (!response.ok) {
				// LSP-18855: on an auth rejection, record WHY before discarding the response. Scribe's body
				// and `www-authenticate` say what it objected to, and the token's own aud/iss/exp say
				// whether we sent the wrong audience, the wrong issuer, or an already-expired token — the
				// three hypotheses this failure has to be separated into. Wrapped so a diagnostic can
				// never be the thing that breaks the reconnect it is describing.
				if (response.status === 401 || response.status === 403) {
					// NOT awaited (ls-heroku-ci LOW): awaiting this delayed every reconnect by up to the
					// diagnostic's own timeout, which contradicted the guarantee stated above it. The
					// reconnect now proceeds immediately and the diagnostic completes on its own.
					//
					// The controller is captured BY VALUE. `abortController` is reassigned by the next
					// connect attempt, so a closure reading the live variable would abort whichever
					// connection happens to be current when the timeout fires — killing a healthy
					// reconnect instead of the stalled read it was meant to release.
					const controllerForThisAttempt = abortController;
					const diagnosticContext = {
						status: response.status,
						statusText: response.statusText,
						wwwAuthenticate: response.headers.get("www-authenticate") ?? "(absent)",
						organizationId: useAuthSessionStore().organizationId ?? "(none)",
						usingDevToken: Boolean(window.env.VITE_SCRIBE_DEV_TOKEN),
						...describeTokenForDiagnostics(token)
					};
					void readBoundedBody(response, () => controllerForThisAttempt?.abort())
						.then((body) => {
							console.error("[Events SSE] Auth rejection on connect", { ...diagnosticContext, body });
						})
						.catch((diagnosticErr) => {
							console.warn("[Events SSE] Could not build the auth-rejection diagnostic", diagnosticErr);
						});
				}
				throw new Error(`SSE connection failed: ${response.status} ${response.statusText}`);
			}

			if (!response.body) {
				throw new Error("Response body is null");
			}

			reader = response.body.getReader();
			const decoder = new TextDecoder();
			let buffer = "";

			isConnected.value = true;

			haltedForSessionLost = false;
			isConnecting.value = false;
			reconnectAttempts.value = 0;
			console.log("[Events SSE] Connected");

			// AC-1: Start heartbeat monitor to detect stale connections
			startHeartbeatMonitor();
			// AC-4: Start connection age timer for periodic reconnection
			startConnectionAgeTimer();

			const readStream = async () => {
				try {
					while (true) {
						if (!reader) break;
						const { done, value } = await reader.read();
						if (done) break;

						buffer += decoder.decode(value, { stream: true });
						const lines = buffer.split("\n");
						buffer = lines.pop() || "";

						for (const line of lines) {
							if (!line.startsWith("data: ")) continue;
							try {
								const data: ScribeEvent = JSON.parse(line.slice(6));
								handleEvent(data);
							} catch (parseErr) {
								console.error("[Events SSE] Error parsing event data:", parseErr);
							}
						}
					}
				} catch (streamErr) {
					if (isExpectedDisconnect(streamErr)) {
						console.warn("[Events SSE] Network error reading stream:", streamErr);
					} else {
						console.error("[Events SSE] Error reading stream:", streamErr);
					}
					throw streamErr;
				} finally {
					stopHeartbeatMonitor();
					stopConnectionAgeTimer();
					isConnected.value = false;
					isConnecting.value = false;
					if (!isIntentionalTeardown && currentEpoch === connectionEpoch) {
						attemptReconnect();
					}
				}
			};

			void readStream().catch((streamErr) => {
				if (isExpectedDisconnect(streamErr)) {
					console.warn("[Events SSE] Stream network error:", streamErr);
				} else {
					console.error("[Events SSE] Stream error:", streamErr);
				}
				isConnected.value = false;
				isConnecting.value = false;
				if (!isIntentionalTeardown && currentEpoch === connectionEpoch) {
					error.value = streamErr instanceof Error ? streamErr : new Error("Stream error");
					attemptReconnect();
				}
			});
		} catch (connectErr) {
			if (isExpectedDisconnect(connectErr)) {
				console.warn("[Events SSE] Network error connecting:", connectErr);
			} else {
				console.error("[Events SSE] Error connecting:", connectErr);
			}
			isConnecting.value = false;
			if (!isIntentionalTeardown && currentEpoch === connectionEpoch) {
				error.value = connectErr instanceof Error ? connectErr : new Error("Failed to connect to SSE");
				attemptReconnect();
			}
		}
	};

	const disconnect = () => {
		connectionEpoch++;
		isIntentionalTeardown = true;

		if (reconnectTimeout) {
			clearTimeout(reconnectTimeout);
			reconnectTimeout = null;
		}

		stopHeartbeatMonitor();
		stopConnectionAgeTimer();

		if (abortController) {
			abortController.abort();
			abortController = null;
		}

		if (reader) {
			reader.cancel().catch(() => {
				// ignore
			});
			reader = null;
		}

		isConnected.value = false;
		isConnecting.value = false;
		reconnectAttempts.value = 0;
		connectionId.value = null;
		lastActivityTime.value = 0;
		console.log("[Events SSE] Disconnected");
	};

	// LSP-16971: resume SSE on IN-PLACE session recovery. If we halted because the session was
	// terminally lost and it later clears WITHOUT a page reload (a background/cross-tab token refresh
	// re-arms auth and clears sessionLost), reconnect — otherwise SSE would stay dark until some
	// unrelated caller happens to call connect() (Codex).
	watch(
		() => useAuthSessionStore().sessionLost,
		(lost) => {
			if (!lost && haltedForSessionLost && !isConnected.value && !isConnecting.value) {
				haltedForSessionLost = false;
				reconnectAttempts.value = 0;
				console.log("[Events SSE] Session recovered in place — reconnecting");
				void connect();
			}
		}
	);

	// The server binds this connection's id to the user+org that opened it and
	// validates that binding on requests carrying X-SSE-Connection-Id. Switching
	// organizations happens in place (no reload), so without this the connection
	// stays bound to the OLD org and those requests start failing with 403
	// "Connection ID does not belong to authenticated user" — while events
	// published to the new org never reach this tab.
	watch(
		() => useAuthSessionStore().organizationId,
		(next, prev) => {
			if (!prev || !next || next === prev) return;
			if (!isConnected.value && !isConnecting.value) return;
			console.log("[Events SSE] Organization changed — rebinding connection");
			// Results for anything already streaming (e.g. an in-flight prompt
			// review) are published to the OLD org's connections and can never
			// reach this tab again — let those consumers fail fast instead of
			// hanging to their timeout.
			window.dispatchEvent(new CustomEvent("scribe:connection-rebound", { detail: { reason: "org-switch" } }));
			forceReconnect();
		}
	);

	return {
		isConnected,
		isConnecting,
		error,
		connectionId,
		lastActivityTime,
		connect,
		disconnect,
		forceReconnect,
		addHandlers
	};
});
